import { useCallback, useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  approveMonteurProposal,
  fetchPendingMonteurProposals,
  rejectMonteurProposal,
  type PlanungMonteurProposalListItem,
} from "@/lib/montageplanung";
import { formatDateDe } from "./dateUtils";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

function formatRequestedAt(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function participantLabel(
  name: string | null | undefined,
  kind?: string | null,
): string {
  const label = (name || "").trim();
  if (!label) return "";
  return kind === "resource" ? `Ressource ${label}` : label;
}

function plannedForLabel(request: PlanungMonteurProposalListItem): string {
  const names = [
    participantLabel(request.row_label, request.row_kind),
    ...(request.invitees ?? []).map((person) =>
      participantLabel(
        person.employee_display || person.row_label || person.employee_login,
        person.kind,
      ),
    ),
  ].filter(Boolean);
  return names.join(", ");
}

function requesterLabel(request: PlanungMonteurProposalListItem): string {
  const display = request.employee_display?.trim();
  if (display && display.toLowerCase() !== request.employee_login.toLowerCase()) {
    return `${display} (${request.employee_login})`;
  }
  return display || request.employee_login;
}

export interface MonteurProposalsDialogProps {
  open: boolean;
  onClose: () => void;
  onReviewed?: () => void;
  onHighlightInPlan?: (request: PlanungMonteurProposalListItem) => void;
}

export function MonteurProposalsDialog({
  open,
  onClose,
  onReviewed,
  onHighlightInPlan,
}: MonteurProposalsDialogProps) {
  const [requests, setRequests] = useState<PlanungMonteurProposalListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRequests(await fetchPendingMonteurProposals());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Terminanträge konnten nicht geladen werden.",
      );
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  async function handleApprove(request: PlanungMonteurProposalListItem) {
    setApprovingId(request.id);
    setError(null);
    try {
      await approveMonteurProposal(request.id);
      onReviewed?.();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Annahme fehlgeschlagen.");
    } finally {
      setApprovingId(null);
    }
  }

  async function handleReject(request: PlanungMonteurProposalListItem) {
    const ok = window.confirm(
      `Terminantrag von ${requesterLabel(request)} für „${request.title}" wirklich ablehnen?`,
    );
    if (!ok) return;
    setRejectingId(request.id);
    setError(null);
    try {
      await rejectMonteurProposal(request.id);
      onReviewed?.();
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Ablehnung fehlgeschlagen.");
    } finally {
      setRejectingId(null);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Terminanträge von Monteuren"
      size="md"
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      {error ? (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : requests.length === 0 ? (
        <p className="py-6 text-center text-sm text-label-secondary">
          Keine offenen Terminanträge.
        </p>
      ) : (
        <ul className="space-y-3">
          {requests.map((request) => {
            const busy = approvingId === request.id || rejectingId === request.id;
            return (
              <li
                key={request.id}
                className="rounded-ios-md border border-separator bg-surface-secondary/40 px-3 py-3"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-label">{request.title}</p>
                    <p className="mt-1 text-xs text-label-secondary">
                      {request.requester_included === false ? (
                        <>
                          Beantragt von {requesterLabel(request)}
                          {plannedForLabel(request) ? ` für ${plannedForLabel(request)}` : ""}
                        </>
                      ) : (
                        <>
                          {requesterLabel(request)}
                          {request.row_label ? ` · ${request.row_label}` : ""}
                        </>
                      )}
                    </p>
                    {request.requester_included !== false && (request.invitees?.length ?? 0) > 0 ? (
                      <p className="mt-1 text-xs text-label-secondary">
                        mit{" "}
                        {request.invitees
                          ?.map((person) =>
                            participantLabel(
                              person.employee_display || person.row_label || person.employee_login,
                              person.kind,
                            ),
                          )
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    ) : null}
                    {(request.orders?.length ?? 0) > 0 ? (
                      <p className="mt-1 text-xs text-label-secondary">
                        {request.orders
                          ?.map((order) => order.order_label || `Auftrag ${order.order_id}`)
                          .join(" · ")}
                      </p>
                    ) : null}
                    <p className="mt-1 text-xs text-label-secondary">
                      {formatDateDe(request.date_from)}
                      {request.date_to !== request.date_from
                        ? ` – ${formatDateDe(request.date_to)}`
                        : ""}
                      {" · "}
                      beantragt {formatRequestedAt(request.requested_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {onHighlightInPlan ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => onHighlightInPlan(request)}
                      >
                        Im Plan zeigen
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={rejectingId === request.id}
                      disabled={busy}
                      onClick={() => void handleReject(request)}
                    >
                      Ablehnen
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      loading={approvingId === request.id}
                      disabled={busy}
                      onClick={() => void handleApprove(request)}
                    >
                      Annehmen
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
}
